import React from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#f6f7f9",
    fontFamily:
      '"Gotham", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    padding: "24px",
    textAlign: "center",
  },
  illustration: {
    marginBottom: 32,
  },
  code: {
    fontSize: 72,
    fontWeight: 700,
    color: "#e2e8f0",
    lineHeight: 1,
    margin: 0,
    letterSpacing: "-0.04em",
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#0f172a",
    margin: "16px 0 8px",
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.6,
    margin: "0 0 32px",
    maxWidth: 400,
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap" as const,
    justifyContent: "center",
  },
  primaryBtn: {
    height: 40,
    padding: "0 24px",
    borderRadius: 10,
    border: "1px solid #0f172a",
    background: "#0f172a",
    color: "#ffffff",
    font: '600 13px/1 "Gotham", system-ui, sans-serif',
    cursor: "pointer",
    transition: "background 0.14s, box-shadow 0.14s",
  },
  secondaryBtn: {
    height: 40,
    padding: "0 24px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#111827",
    font: '600 13px/1 "Gotham", system-ui, sans-serif',
    cursor: "pointer",
    transition: "background 0.14s, border-color 0.14s",
  },
};

function NotFoundPage() {
  const navigate = useNavigate();
  const { user, loading } = useUser();

  return (
    <div style={styles.wrapper}>
      {/* SVG illustration: broken link / lost page */}
      <div style={styles.illustration}>
        <svg
          width="120"
          height="120"
          viewBox="0 0 120 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Page shape */}
          <rect
            x="30"
            y="14"
            width="60"
            height="78"
            rx="6"
            fill="#e2e8f0"
            stroke="#cbd5e1"
            strokeWidth="2"
          />
          {/* Folded corner */}
          <path d="M72 14 L90 32 L72 32 Z" fill="#cbd5e1" />
          {/* Text lines */}
          <rect x="40" y="42" width="32" height="4" rx="2" fill="#cbd5e1" />
          <rect x="40" y="52" width="24" height="4" rx="2" fill="#cbd5e1" />
          <rect x="40" y="62" width="28" height="4" rx="2" fill="#cbd5e1" />
          {/* Question mark */}
          <circle cx="60" cy="104" r="14" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2" />
          <text
            x="60"
            y="110"
            textAnchor="middle"
            fontFamily="system-ui, sans-serif"
            fontSize="18"
            fontWeight="700"
            fill="#94a3b8"
          >
            ?
          </text>
        </svg>
      </div>

      <p style={styles.code}>404</p>
      <h1 style={styles.title}>Page not found</h1>
      <p style={styles.subtitle}>
        The page you're looking for doesn't exist or has been moved.
      </p>

      {!loading && (
        <div style={styles.actions}>
          {user ? (
            <>
              <button
                style={styles.primaryBtn}
                onClick={() => navigate("/dashboard")}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#020617";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    "0 6px 18px rgba(15,23,42,0.35)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#0f172a";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
                }}
              >
                Go to Dashboard
              </button>
              <button
                style={styles.secondaryBtn}
                onClick={() => navigate("/")}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#f9fafb";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#111827";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#ffffff";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db";
                }}
              >
                Go Home
              </button>
            </>
          ) : (
            <>
              <button
                style={styles.primaryBtn}
                onClick={() => navigate("/")}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#020617";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    "0 6px 18px rgba(15,23,42,0.35)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#0f172a";
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
                }}
              >
                Go Home
              </button>
              <button
                style={styles.secondaryBtn}
                onClick={() => navigate("/?modal=login")}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#f9fafb";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#111827";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "#ffffff";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "#d1d5db";
                }}
              >
                Log In
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default NotFoundPage;
